import type { Primitive } from '@root/src/hooks/useUrlFilters';

type ApiFilters = Record<string, Primitive | Primitive[]>;

interface RangeKeys {
  /** Filter key holding the lower bound, e.g. `createdAtMin`. */
  min: string;
  /** Filter key holding the upper bound, e.g. `createdAtMax`. */
  max: string;
  /** Filter key the backend reads the `{ min, max }` range from, e.g. `createdAt`. */
  into: string;
}

/**
 * Folds two flat bound filters (kept as two URL keys) into the single `{ min, max }`
 * filter the backend metafilters read. A missing bound is left out of the range;
 * no bound at all sends no range filter. The range goes last, so the `filtered`
 * query param keeps the order of the other filters.
 *
 * Backend readers of `createdAt: { min, max }`:
 * - src/modules/back/ssts/sstsMetafilters.js:90
 * - src/modules/back/dedicated-forums/dedicatedForumsMetafilters.js:168
 */
export const foldRangeFilter = (apiFilters: ApiFilters, keys: RangeKeys): ApiFilters => {
  const { [keys.min]: min, [keys.max]: max, ...rest } = apiFilters;

  if (!min && !max) {
    return rest;
  }

  return {
    ...rest,
    // The range is the one nested filter value; the flat filter type has no room for it.
    [keys.into]: { ...(min && { min }), ...(max && { max }) } as unknown as Primitive,
  };
};
